import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import type { AccountSubscriptionInfoResponseDto } from '@sharedlib/dtos/dto/accounts/account-info.dto';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { FRONTEND_APP } from '../providers/frontend-app.token';
import { AccountService } from '../services/account.service';
import { AuthService } from '../services/auth.service';
import { redirectToLoginApp, redirectToMainApp } from '../utils/cross-app-nav';

type UserState =
  | { readonly kind: 'unauthenticated' }
  | { readonly kind: 'unverified' }
  | { readonly kind: 'verified'; readonly info: AccountSubscriptionInfoResponseDto };

/**
 * Resolves the caller's checkout state, emitting once. A transient account-info failure degrades to
 * `unverified` (fail-open): a paid user is never locked out nor ping-ponged between guards, and
 * entitlements are enforced server-side regardless.
 */
function resolveUserState(): Observable<UserState> {
  const authService = inject(AuthService);
  const accountService = inject(AccountService);

  return authService.getAuthenticated().pipe(
    take(1),
    switchMap((authenticated) => {
      if (!authenticated) {
        return of<UserState>({ kind: 'unauthenticated' });
      }
      if (authenticated.recoveryEmailVerified !== true) {
        return of<UserState>({ kind: 'unverified' });
      }
      return accountService.getAccountInfo().pipe(
        map((info): UserState => ({ kind: 'verified', info })),
        catchError(() => of<UserState>({ kind: 'unverified' })),
      );
    }),
  );
}

/**
 * Main-app gate: a verified account still requiring checkout is sent to the login app's payment page
 */
export const checkoutRequiredGuard: CanActivateFn = () => {
  const app = inject(FRONTEND_APP);
  const router = inject(Router);

  return resolveUserState().pipe(
    map((state) =>
      state.kind === 'verified' && state.info.checkoutRequired
        ? redirectToLoginApp(app, router, '/checkout/payment')
        : true,
    ),
  );
};

/**
 * Login `/checkout` gate: a verified account that no longer requires checkout is sent back to the
 * main app (nothing to pay for)
 */
export const checkoutAccessGuard: CanActivateFn = () => {
  const app = inject(FRONTEND_APP);
  const router = inject(Router);

  return resolveUserState().pipe(
    map((state) => (state.kind === 'verified' && !state.info.checkoutRequired ? redirectToMainApp(app, router) : true)),
  );
};
